import { equalInLC, FCX, getFormattedThousand, ID, useOutsideClick } from "@datassential/platform-ui-lib";
import './SocialAssociations.scss';
import BarChart from "@pages/Food/components/BarChart/BarChart";
import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import Widget from "@components/Widget/Widget";
import FoodSocialCustomAssociation
    from "@pages/Food/components/SectionSocial/components/SocialCustomAssociation/SocialCustomAssociation";
import cn from "classnames";
import { IFoodSocialCustomAssociation } from "@models/FoodSocialCustomAssociation";

const Item: FCX<{
    name: string;
    value: number;
    proportionalValue: number;
    growthPct: number;
    index: number;
    onClick: (word: string) => void;
    isSelected?: boolean;
}> = ({
    name,
    value,
    proportionalValue,
    growthPct,
    index,
    onClick,
    isSelected,
}) => {
    return (
        <div
            className={cn(
                "FoodSocialAssociations__item is-clickable FoodSectionSocial__blur",
                isSelected && "is-selected",
            )}
            onClick={() => onClick(isSelected ? '' : name)}
        >
            <div
                className="FoodSocialAssociations__item-title"
                data-testid={`textFoodSocialAssociationsItemName-${index}`}
            >
                {name}
            </div>
            <div className="FoodSocialAssociations__item-content">
                <BarChart
                    valuePct={proportionalValue}
                    className="FoodSocialAssociations__item-bar"
                />
                <div
                    className="FoodSocialAssociations__item-value is-center is-w-big"
                    data-testid={`textFoodSocialAssociationsItemCount-${index}`}
                >
                    {getFormattedThousand(value)}
                </div>
                <div
                    className="FoodSocialAssociations__item-value is-center is-w-big"
                    data-testid={`textFoodSocialAssociationsItemGrowth-${index}`}
                >
                    {growthPct > 0 ? '+' : ''}{growthPct}%
                </div>
            </div>
        </div>
    );
};

const ItemsGroupHeader: FCX = () => {
    return (
        <div className="FoodSocialAssociations__item is-no-p is-m-small">
            <div className="FoodSocialAssociations__item-content">
                <div className="FoodSocialAssociations__item-bar"/>
                <div
                    className="FoodSocialAssociations__item-value is-center is-fz-s is-w-big"
                    data-testid="textFoodSocialAssociationsLabelCount"
                >
                    Count
                </div>
                <div
                    className="FoodSocialAssociations__item-value is-center is-fz-s is-w-big"
                    data-testid="textFoodSocialAssociationsLabelGrowth"
                >
                    1-Mo<br/>Growth
                </div>
            </div>
        </div>
    );
};

interface IAssociation {
    id: ID;
    name: string;
    count: number;
    oneMonthGrowthPct: number;
}

const FoodSocialAssociations: FCX<{
    word: string;
    wordId: ID;
    foodAssociations: IAssociation[];
    placesAssociations: IAssociation[];
    selectedAssociation?: string;
    onItemClick: (word: string) => void;
    isCustomAssociationEnabled?: boolean;
    selectedCustomAssociationQuery?: string;
    onCustomAssociationClick?: (result: IFoodSocialCustomAssociation | null) => void;
    /** Optional content above the columns, e.g. a "click any term" hint. */
    hint?: ReactNode;
}> = ({
    word,
    wordId,
    foodAssociations: foodAssociationsRaw,
    placesAssociations: placesAssociationsRaw,
    selectedAssociation,
    onItemClick,
    isCustomAssociationEnabled = false,
    selectedCustomAssociationQuery,
    onCustomAssociationClick,
    hint,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useOutsideClick({
        ref,
        callback: () => setIsExpanded(false),
    });

    const getItems = useCallback(
        (items: IAssociation[]) => {
            const selectedAssociationIndex = items.findIndex(i => i.name === selectedAssociation);
            if (!isExpanded && selectedAssociationIndex > 4) {
                return [
                    ...items.slice(0, 4),
                    items[selectedAssociationIndex],
                ];
            }

            return items.slice(0, isExpanded ? 10 : 5);
        },
        [isExpanded, selectedAssociation],
    );

    const foodAssociations = useMemo(
        () => {
            return getItems(foodAssociationsRaw);
        },
        [foodAssociationsRaw, getItems],
    );
    const placesAssociations = useMemo(
        () => {
            return getItems(placesAssociationsRaw);
        },
        [placesAssociationsRaw, getItems],
    );

    const getProportionalValue = useCallback(
        (v: number, options: IAssociation[]) => {
            return v / Math.max(...options.map(i => i.count)) * 100;
        },
        []
    );

    return (
        <Widget
            className={cn(
                "FoodSocialAssociations",
                isExpanded && "is-expanded",
                isCustomAssociationEnabled && "is-with-custom-association",
            )}
            headerContent={(
                <div
                    className="FoodSectionSocial__widget-title"
                    data-testid="textFoodSocialTopAssociationsTitle"
                >
                    TOP ASSOCIATIONS
                    <div
                        className="Widget__header-sub-title ml"
                        data-testid="textFoodSocialTopAssociationsSubTitle"
                    >
                        &nbsp;mentions past 90 days
                    </div>
                </div>
            )}
        >
            <div
                className="FoodSocialAssociations__content"
                ref={ref}
            >
                {hint && <div className="FoodSocialAssociations__hint">{hint}</div>}
                <div className="FoodSocialAssociations__cols">
                    <div
                        className="FoodSocialAssociations__col"
                        data-testid="blockFoodSocialAssociationsFood"
                    >
                        <div className="FoodSocialAssociations__col-header Widget__header">
                            <div>
                                <b data-testid="textFoodSocialAssociationsFoodLabel">
                                    FOOD
                                </b>
                                &nbsp;+&nbsp;
                                <span data-testid="textFoodSocialAssociationsFoodWordLabel">
                                {word}
                            </span>
                            </div>
                        </div>
                        <div className="FoodSocialAssociations__col-content">
                            <ItemsGroupHeader/>
                            {foodAssociations.map((item, index) => (
                                <Item
                                    key={item.id}
                                    name={item.name}
                                    value={item.count}
                                    proportionalValue={getProportionalValue(item.count, foodAssociations)}
                                    growthPct={item.oneMonthGrowthPct}
                                    index={index}
                                    isSelected={equalInLC(item.name, selectedAssociation)}
                                    onClick={(...args) => {
                                        onItemClick(...args);
                                        setIsExpanded(false);
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                    <div
                        className="FoodSocialAssociations__col"
                        data-testid="blockFoodSocialAssociationsPlaces"
                    >
                        <div className="FoodSocialAssociations__col-header Widget__header">
                            <div>
                                <b data-testid="textFoodSocialAssociationsPlacesLabel">
                                    PLACES
                                </b>
                                &nbsp;+&nbsp;
                                <span data-testid="textFoodSocialAssociationsPlacesWordLabel">
                                {word}
                            </span>
                            </div>
                        </div>
                        <div className="FoodSocialAssociations__col-content">
                            <ItemsGroupHeader/>
                            {placesAssociations.map((item, index) => (
                                <Item
                                    key={item.id}
                                    name={item.name}
                                    value={item.count}
                                    proportionalValue={getProportionalValue(item.count, placesAssociations)}
                                    growthPct={item.oneMonthGrowthPct}
                                    index={index}
                                    isSelected={equalInLC(item.name, selectedAssociation)}
                                    onClick={(...args) => {
                                        onItemClick(...args);
                                        setIsExpanded(false);
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="Widget__footer">
                    <div
                        className="Widget__footer-link"
                        onClick={() => setIsExpanded(!isExpanded)}
                        data-testid="buttonFoodSocialAssociationsExpand"
                    >
                        {isExpanded ? 'show less' : 'show more'}
                    </div>
                </div>

                {isCustomAssociationEnabled && (
                    <FoodSocialCustomAssociation
                        wordId={wordId}
                        selectedQuery={selectedCustomAssociationQuery}
                        onResultClick={onCustomAssociationClick}
                    />
                )}
            </div>
        </Widget>
    );
};

export default FoodSocialAssociations;